'use client';

/**
 * CanadaNav — the wordmark-only nav for the Canadian landing page.
 *
 * The Canadian surface deliberately carries no Invest/Learn/Portfolio/account
 * controls: those route into US offerings and signed-in StartEngine account
 * flows that are not part of the Belco-distributed Canadian experience. The
 * wordmark returns to startengine.ca.
 *
 * Matches the EditorialNav shell (sticky, night surface, same wordmark and
 * padding) so it sits identically above the hero.
 */
import { styled } from '@mui/material/styles';
import { withSe3AssetPrefix } from 'components/HomepageV3/assetPrefix';
import { STARTENGINE_CA_URL } from '../data/canadaSite';

const WORDMARK_SRC = withSe3AssetPrefix('/homepage-v3/se-wordmark.png');

export interface CanadaNavProps {
  /** Wordmark destination. Defaults to the Canadian site. */
  homeHref?: string;
  onCtaClick?: () => void;
  className?: string;
  'data-cy'?: string;
}

const Shell = styled('header')({
  position: 'sticky',
  top: 0,
  zIndex: 1200,
  background: 'var(--night)',
});

const Nav = styled('nav')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '20px var(--pad)',
  borderBottom: '1px solid rgba(233,215,182,0.12)',
  '@media (min-width: 641px) and (max-width: 900px)': {
    padding: '20px 24px',
  },
  '@media (max-width: 640px)': {
    padding: '14px var(--pad)',
  },
});

const Brand = styled('a')({
  display: 'flex',
  alignItems: 'center',
  textDecoration: 'none',
  flexShrink: 0,
  '&:focus-visible': {
    outline: '2px solid var(--champagne)',
    outlineOffset: '3px',
  },
});

const BrandImg = styled('img')({
  height: 30,
  width: 'auto',
  display: 'block',
});

const Cta = styled('button')({
  fontFamily: 'var(--sans)',
  fontSize: 12,
  fontWeight: 600,
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
  color: 'var(--night)',
  background: 'var(--champagne)',
  border: 'none',
  padding: '13px 24px',
  borderRadius: 14,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  transition: 'background 0.15s',
  '&:hover': {
    background: '#f3e6cb',
  },
  '&:focus-visible': {
    outline: '2px solid var(--champagne)',
    outlineOffset: '3px',
  },
  '@media (max-width: 640px)': {
    fontSize: 10,
    padding: '11px 16px',
  },
});

export function CanadaNav({
  homeHref = STARTENGINE_CA_URL,
  onCtaClick,
  className,
  'data-cy': dataCy = 'canada-nav',
}: CanadaNavProps) {
  return (
    <Shell className={className} data-cy={dataCy}>
      <Nav aria-label="Primary" className="se-nav">
        <Brand
          href={homeHref}
          aria-label="StartEngine"
          data-cy="canada-nav-brand"
          className="se-logo"
        >
          <BrandImg className="se-logo-img" src={WORDMARK_SRC} alt="StartEngine" />
        </Brand>
        <Cta type="button" onClick={onCtaClick} data-cy="canada-nav-cta">
          Book a Call
        </Cta>
      </Nav>
    </Shell>
  );
}

export default CanadaNav;
